import { useRequiredContext } from "@redotech/react-util/context";
import { elementIsSubmittableElement } from "@redotech/redo-model/forge/review-forge-types";
import { insert, swap } from "@redotech/util/array";
import { deepCopyObject } from "@redotech/util/object";
import { memo, useContext } from "react";
import {
  RedoButton,
  RedoButtonProps,
} from "../arbiter-components/buttons/redo-button";
import ArrowDownIcon from "../arbiter-icon/arrow-down_filled.svg";
import ArrowUpIcon from "../arbiter-icon/arrow-up_filled.svg";
import CopyIcon from "../arbiter-icon/copy-05.svg";
import TrashIcon from "../arbiter-icon/trash-01.svg";
import { Flex } from "../flex";
import {
  ReviewFormContext,
  ReviewFormSelection,
  SetReviewFormSelectionContext,
  THANK_YOU_UPSELL_PAGE,
} from "./review-form-editor-contexts";
import * as reviewFormPreviewCss from "./review-form-preview.module.css";
import { AllReviewFormElements } from "./review-form-types";

export const ReviewFormElementMenuBar = memo(function ReviewFormElementMenuBar({
  element,
  pageIndex,
  elementIndex,
  maxElementIndex,
}: {
  element: AllReviewFormElements;
  pageIndex: ReviewFormSelection["selectedPageIndex"];
  elementIndex: number;
  maxElementIndex: number;
}) {
  const form = useRequiredContext(ReviewFormContext);
  const setSelectedElement = useContext(SetReviewFormSelectionContext);

  const buttonProps: RedoButtonProps[] = [
    {
      IconLeading: CopyIcon,
      disabled: elementIsSubmittableElement(element),
      onClick: () => {
        const currentPage =
          pageIndex === THANK_YOU_UPSELL_PAGE
            ? form.inputs.thankYouPage
            : form.inputs.pages.inputs[pageIndex];
        const pageElements = currentPage.value.elements;
        const currentElement = pageElements[elementIndex];
        const newElement = deepCopyObject(currentElement);
        const insertedNewElements = insert(
          pageElements,
          elementIndex + 1,
          newElement,
        );
        currentPage.setValue({ elements: insertedNewElements as any });
        setSelectedElement((old) => {
          return {
            selectedPageIndex: old.selectedPageIndex,
            selectedElementIndex: elementIndex + 1,
          };
        });
      },
    },
    {
      theme: "destructive",
      IconLeading: TrashIcon,
      onClick: () => {
        const currentPage =
          pageIndex === THANK_YOU_UPSELL_PAGE
            ? form.inputs.thankYouPage
            : form.inputs.pages.inputs[pageIndex];
        const newElements = currentPage.value.elements.filter(
          (_, i) => i !== elementIndex,
        );
        currentPage.setValue({ elements: newElements as any });
        setSelectedElement((old) => {
          return {
            selectedPageIndex: old.selectedPageIndex,
            selectedElementIndex: null,
          };
        });
      },
    },
    {
      IconLeading: ArrowUpIcon,
      onClick: () => {
        const newIndex = elementIndex - 1;
        const currentPage =
          pageIndex === THANK_YOU_UPSELL_PAGE
            ? form.inputs.thankYouPage
            : form.inputs.pages.inputs[pageIndex];
        if (elementIndex !== 0) {
          const newElements = swap(
            currentPage.value.elements as any,
            elementIndex,
            newIndex,
          );
          currentPage.setValue({ elements: newElements as any });
          setSelectedElement((old) => {
            return {
              selectedPageIndex: old.selectedPageIndex,
              selectedElementIndex: newIndex,
            };
          });
        }
      },
    },
    {
      IconLeading: ArrowDownIcon,
      onClick: () => {
        const newIndex = elementIndex + 1;
        const currentPage =
          pageIndex === THANK_YOU_UPSELL_PAGE
            ? form.inputs.thankYouPage
            : form.inputs.pages.inputs[pageIndex];
        if (elementIndex !== maxElementIndex) {
          const newElements = swap(
            currentPage.value.elements as any,
            elementIndex,
            newIndex,
          );
          currentPage.setValue({ elements: newElements as any });
          setSelectedElement((old) => {
            return {
              selectedPageIndex: old.selectedPageIndex,
              selectedElementIndex: newIndex,
            };
          });
        }
      },
    },
  ];

  return (
    <Flex
      alignSelf="center"
      bgColor="secondary"
      className={reviewFormPreviewCss.pageMenu}
      dir="column"
      radius="md"
    >
      {buttonProps.map((buttonProp, i) => (
        <RedoButton size="lg" {...buttonProp} key={i} />
      ))}
    </Flex>
  );
});

export const ReviewFormPageMenuBar = memo(function ReviewFormPageMenuBar({
  pageIndex,
  maxPageIndex,
}: {
  pageIndex: number;
  maxPageIndex: number;
}) {
  const form = useRequiredContext(ReviewFormContext);

  const setSelectedElement = useContext(SetReviewFormSelectionContext);

  const buttonProps: RedoButtonProps[] = [
    {
      IconLeading: CopyIcon,
      onClick: () => {
        const allPages = form.value.pages;
        const currentPage = allPages[pageIndex];
        const newPage = deepCopyObject(currentPage);
        const insertedNewPages = insert(allPages, pageIndex + 1, newPage);
        form.inputs.pages.setValue(insertedNewPages);
        setSelectedElement({
          selectedPageIndex: pageIndex + 1,
          selectedElementIndex: null,
        });
      },
    },
    {
      theme: "destructive",
      IconLeading: TrashIcon,
      onClick: () => {
        const allPages = form.value.pages;
        const newPages = allPages.filter((_, i) => i !== pageIndex);
        form.inputs.pages.setValue(newPages);
      },
    },
    {
      IconLeading: ArrowUpIcon,
      onClick: () => {
        const newIndex = pageIndex - 1;
        if (pageIndex !== 0) {
          const newPages = swap(form.value.pages, pageIndex, newIndex);
          form.inputs.pages.setValue(newPages);
          setSelectedElement({
            selectedPageIndex: newIndex,
            selectedElementIndex: null,
          });
        }
      },
    },
    {
      IconLeading: ArrowDownIcon,
      onClick: () => {
        const newIndex = pageIndex + 1;
        if (pageIndex !== maxPageIndex) {
          const newPages = swap(form.value.pages, pageIndex, newIndex);
          form.inputs.pages.setValue(newPages);
          setSelectedElement({
            selectedPageIndex: newIndex,
            selectedElementIndex: null,
          });
        }
      },
    },
  ];

  return (
    <Flex alignSelf="center" bgColor="secondary" dir="column" radius="md">
      {buttonProps.map((buttonProp, i) => (
        <RedoButton size="lg" {...buttonProp} key={i} />
      ))}
    </Flex>
  );
});
